<script setup lang="ts">
import type { UseFinancingData } from '~/store-v2/financings/useFinancing'
import type { UseHardwareFormData } from '~/store-v2/hardware/useHardwareForm'
import type { UseRegistrationData } from '~/store-v2/registration/useRegistration'
import type { Error, StageFormalization } from '~/ui-blocks/Formalization/Formalization.types'
import type { BiometryAlertStatus } from '~/utils/customer-register/Biometry'
import { defineComponent } from 'vue'
import OnlyBatteryCompatibilityAlert from '~/components/only-battery/OnlyBatteryCompatibilityAlert.vue'
import TurboPrimeAlert from '~/components/turbo-prime/TurboPrimeAlert.vue'
import { usePendingUpdateEquipment } from '~/composables/equipment/usePendingUpdateEquipment'
import { useCancellationAlert } from '~/composables/useCancellationAlert'
import { isStageReproved } from '~/services-v2/financial-bff/financial-bff.utils'
import useCustomerBiometry from '~/store-v2/contract-formalization/useCustomerBiometry'
import useFinancing from '~/store-v2/financings/useFinancing'
import useRegistration from '~/store-v2/registration/useRegistration'
import Equipment from '~/ui-blocks/Equipment/Equipment.vue'
import RegistrationForms from '~/ui-blocks/Formalization/1.RegistrationPJ.vue'
import LackBankDetails from '~/ui-blocks/Formalization/blocks/LackBankDetails.vue'
import FormalizationReiceiptModel from '~/ui-blocks/Formalization/blocks/paymentModel/PaymentModelStep.vue'
import { extractGloballErrors } from '~/ui-blocks/Formalization/Formalization.utils'
import FormalizationBiometry from '~/ui-blocks/FormalizationBiometry/FormalizationBiometry.vue'
import date from '~/utils/date'
import { GeneralStatuses } from '~/utils/financing-flow/Financing'

const props = defineProps<{
  registration: UseRegistrationData
  triggerRefetch: () => Promise<void>
  isOnflexPartner: boolean
  projectName: string
  hardwareForm: {
    response?: UseHardwareFormData
    refetch: () => Promise<any>
    isLoading: boolean
    isError: boolean
  }
  hasEnergyBillAnalysis: boolean
  systemPower?: number
  isTurboPrime?: boolean
}>()

defineComponent({
  components: {
    RegistrationForms,
    FormalizationBiometry,
  },
})

const { t } = useI18n()
const router = useRouter()
const route = useRoute<any>()

const id = computed(() => route.params.id as string)
const showAlertAgainAnalysisBill = ref(false)
const showModalLackBankDetails = ref(false)
const statusDocument = ref('' as string)

const biometryAlert = ref({
  pendencies: null,
  reproved: false,
  biometryStatus: '',
} as unknown as BiometryAlertStatus)

const stage = ref('') as unknown as Ref<StageFormalization>

const {
  isLoading: isLoadingStatus,
  refetch: refetchStatus,
  data: financingDetails,
} = useFinancing(id)

const isContractStepAvailable = computed(() => {
  return !!financingDetails.value?.openable_steps?.contract
})

const { data: customerBiometry } = useCustomerBiometry(id, isContractStepAvailable)

const { cancellationAlert } = useCancellationAlert(customerBiometry)

const { hasPendingTAF } = usePendingUpdateEquipment(id)

const {
  data: registration,
} = useRegistration(id)

const hasAlert = props.registration?.status === 'reproved'
  || statusDocument.value === 'failed'
  || biometryAlert.value?.reproved
  || biometryAlert.value?.pendencies?.items?.length
  || financingDetails.value?.status === 'reproved'
  || financingDetails.value?.status === 'canceled'

const stageReproved = computed(() => {
  return financingDetails.value && financingDetails.value.section_statuses
    ? isStageReproved(financingDetails.value.section_statuses)
    : ''
})

const componentStage = ref({
  hardware: {
    id: 'hardware',
    name: 'CompanyProjectData',
    component: Equipment,
    props: {
      projectName: props.projectName,
    },
  },
  registration: {
    id: 'registration',
    name: 'RegistrationForms',
    component: RegistrationForms,
    props: {
      registration: props.registration,
      triggerRefetch: props.triggerRefetch,
      statusDocument,
    },
  },
  contract: {
    id: 'contract',
    name: 'FormalizationBiometry',
    component: FormalizationBiometry,
    props: {},
  },
  receipt_model: {
    id: 'receipt_model',
    name: 'FormalizationReiceiptModel',
    component: FormalizationReiceiptModel,
    props: {
      isOnflexPartner: props.isOnflexPartner,
    },
  },
})

const components = ref([
  componentStage.value.registration,
  componentStage.value.contract,
  componentStage.value.hardware,
  componentStage.value.receipt_model,
])

function stageOpenCollapse(value: StageFormalization) {
  stage.value = value

  setTimeout(() => {
    const element = document.getElementById(`accordion_${value}`)
    if (element)
      element.scrollIntoView({ behavior: 'smooth' })
  }, 500)
}

function redirectPage(value: string) {
  const routes = {
    simulation: `/simulation/details/${id.value}`,
    installation: `/installation/${id.value}`,
    concluded: `/installation/${id.value}`,
  }

  if (routes[value])
    router.push(routes[value])
}

function openWhatsApp() {
  const whatsappLink = 'https://wa.me/5511912386374?text=Ol%C3%A1!%20Preciso%20de%20ajuda%20para%20atualizar%20meus%20dados%20banc%C3%A1rios%20na%20plataforma.'
  window.open(whatsappLink, '_blank')
}

function handleBiometryAlert(value: BiometryAlertStatus) {
  biometryAlert.value = value
}

function handleClientStatusDocumentStep(value: string) {
  statusDocument.value = value
}

function handleShowAlertAgainAnalysisBill() {
  showAlertAgainAnalysisBill.value = true
}

const disabledGoToInstallation = computed(() => {
  if (hasPendingTAF.value)
    return true

  if (['installation', 'concluded'].includes(financingDetails?.value?.step ?? ''))
    return false
  return true
})

const installationButtonTooltip = computed(() => {
  if (hasPendingTAF.value) {
    return t('inversorInstallation.form.taf_pending_tooltip')
  }
  return undefined
})

const alert = computed(() => {
  if (['approved', 'canceled', 'expired'].includes(financingDetails.value?.status ?? ''))
    return null
  return extractGloballErrors(registration.value)
})

const shouldGoToWhatsApp = computed(() => ['generic.qi_tech_account', 'generic.bank_account'].includes(alert.value?.field ?? ''))

const statusToNavigationHeader = computed(() => financingDetails.value as UseFinancingData)

const underAnalysisAlert = computed(() => {
  const statusNotCanceled = financingDetails.value?.status !== 'canceled'

  if (financingDetails.value?.section_statuses?.customer_data?.status === GeneralStatuses.APPROVED) {
    return null
  }

  if (financingDetails.value?.section_statuses?.customer_data?.status === GeneralStatuses.UNDER_ANALYSIS && statusNotCanceled) {
    return {
      title: 'Seu projeto passará por uma análise manual feita por um de nossos especialistas',
      description: 'Leituras manuais podem acontecer por diversos fatores como legibilidade e confirmação de dados. Logo você será avisado do resultado!',
      type: 'informative',
      field: 'under_analysis',
    } as Error
  }

  return null
})

watch(alert, (value) => {
  if (value) {
    showModalLackBankDetails.value = value.showModal
  }
}, { immediate: true })

watch(() => props.hardwareForm.response, async () => {
  let index = 0 as number
  const item = 'hardware'

  components.value.forEach((component, key) => {
    if (component.id === item)
      index = key
  })

  components.value = components.value.filter(component => component.id !== item)

  await nextTick()
  components.value.splice(index, 0, componentStage.value[item])
})

watch(() => props.registration, async () => {
  componentStage.value.registration.props.registration = props.registration
})
</script>

<template>
  <div v-if="!isLoadingStatus">
    <NavigationHeaderBar
      stage="proposal"
      :title="props.projectName"
      :project-status="statusToNavigationHeader"
      :financing="financingDetails"
      :system-power="systemPower"
      :is-turbo-prime="isTurboPrime"
      @stage="stageOpenCollapse"
    />

    <TurboPrimeAlert v-if="isTurboPrime === true" />

    <OnlyBatteryCompatibilityAlert :project-id="financingDetails?.project_id" />

    <div v-if="components">
      <template v-if="alert">
        <SolAlert
          id="feedback"
          class="my-4"
          :feedback="(alert.type as any)"
          :title="alert.title"
          :action="shouldGoToWhatsApp ? 'Atualizar cadastro' : ''"
          @click="shouldGoToWhatsApp ? openWhatsApp() : () => {}"
        >
          {{ alert.description }}
        </SolAlert>
      </template>

      <template v-if="underAnalysisAlert">
        <SolAlert
          id="feedback"
          class="my-4"
          :feedback="(underAnalysisAlert.type as any)"
          :title="underAnalysisAlert.title"
        >
          {{ underAnalysisAlert.description }}
        </SolAlert>
      </template>

      <SolAlert
        v-if="showAlertAgainAnalysisBill || financingDetails?.reason === 'analyze_energy'"
        id="alert-analysis-bill-pj"
        :title="t('financing.alert.bill.title')"
        feedback="error"
        class="my-4"
      >
        {{ t('financing.alert.bill.description') }}
      </SolAlert>

      <div v-if="hasAlert" class="my-4 mb-8">
        <template v-if="financingDetails?.status === 'canceled'">
          <SolAlert
            v-if="cancellationAlert"
            id="informative-feedback-canceled"
            :title="cancellationAlert.title"
            feedback="error"
            class="my-4"
          >
            {{ cancellationAlert.description }}
          </SolAlert>
        </template>

        <template v-if="financingDetails?.status === 'reproved' && financingDetails?.reason !== 'analyze_energy'">
          <template v-if="biometryAlert?.reproved">
            <SolAlert
              v-if="biometryAlert?.biometryStatus === 'reproved'"
              id="reproved-feedback-biometry"
              :title="t('customer.biometry.alert.reproved_biometry.title')"
              feedback="error"
              class="my-4"
            >
              {{ t('customer.biometry.alert.reproved_biometry.subtitle') }}
            </SolAlert>
            <solAlert
              v-else
              id="reproved-feedback-contract"
              :title="t('customer.biometry.alert.reproved.title')"
              feedback="error"
              class="my-4"
            >
              {{ t('customer.biometry.alert.reproved.subtitle') }}
            </solAlert>
          </template>

          <div v-for="(item, index) in biometryAlert?.pendencies?.items" :key="index">
            <SolAlert
              id="pendencies-feedback-biometry"
              class="my-4"
              :title="item.message"
              feedback="warning"
            >
              {{ t('customer.biometry.alert.pendencies',
                   { date: date(biometryAlert?.pendencies?.expired_at).format("DD/MM/YYYY") })
              }}
            </SolAlert>
          </div>
        </template>
      </div>

      <component
        :is="component.component"
        v-for="(component, index) in components"
        :key="index"
        :position="index + 1"
        client-status="approved"
        :status-project="financingDetails?.section_statuses"
        :trigger-refetch="props.triggerRefetch"
        :stage="stage"
        :stage-reproved="stageReproved"
        :form-data="props.hardwareForm.response"
        :refetch="props.hardwareForm.refetch"
        :is-hardware-form-loading="component.id === 'hardware' ? props.hardwareForm.isLoading : undefined"
        :is-hardware-form-error="component.id === 'hardware' ? props.hardwareForm.isError : undefined"
        v-bind="component.props"
        @status-biometry="refetchStatus"
        @alert-biometry="handleBiometryAlert"
        @client-status-document-step="handleClientStatusDocumentStep"
        @show-alert-again-analysis-bill="handleShowAlertAgainAnalysisBill"
      />
    </div>

    <div class="mx-4 md:system:mx-12">
      <SolDivider thickness="x-small" orientation="horizontal" class="my-5 mt-0 md:system:my-0" />

      <div class="flex flex-col md:system:flex-row flex-col-reverse justify-between gap-5 my-4 md:system:my-12">
        <SolButton
          id="customer-back-simulation"
          size="large"
          class="w-full md:system:w-auto"
          variant="secondary"
          @click="redirectPage('simulation')"
        >
          {{ t('customer.endButtons.backSimulation') }}
        </SolButton>

        <Tooltip
          v-if="installationButtonTooltip"
          position="top"
          width="280px"
        >
          <template #text>
            {{ installationButtonTooltip }}
          </template>
          <SolButton
            id="customer-next-installation"
            size="large"
            class="w-full md:system:w-auto"
            :disabled="disabledGoToInstallation"
            @click="redirectPage('installation')"
          >
            {{ t('customer.endButtons.nextInstallation') }}
          </SolButton>
        </Tooltip>
        <SolButton
          v-else
          id="customer-next-installation"
          size="large"
          class="w-full md:system:w-auto"
          :disabled="disabledGoToInstallation"
          @click="redirectPage('installation')"
        >
          {{ t('customer.endButtons.nextInstallation') }}
        </SolButton>
      </div>
    </div>
  </div>
  <LackBankDetails
    :show="showModalLackBankDetails"
    @close="showModalLackBankDetails = false"
  />
</template>
